/**
 * Utility for formatting axis labels with intelligent text wrapping and truncation
 */

interface FormatLabelOptions {
  value: string;
  dataLength: number;
  isFullscreen?: boolean;
  maxCharsPerLine?: number;
  maxLines?: number;
  maxFeaturesToAllowTextWrap?: number;
}

/**
 * Strips the _CODE suffix from categorical feature names for display purposes.
 *
 * @param featureName - The feature name that may contain _CODE suffix
 * @returns Feature name without the _CODE suffix
 */
export function stripCodeSuffix(featureName: string): string {
  return featureName.replace(/_CODE$/, "");
}

const NORMALIZED_BY_DELIMITER = " normalized by ";

/**
 * Extracts the main label and a display-friendly normalization suffix from a label string.
 * Converts " normalized by X" into "per X" for compact chart axis display.
 *
 * @param label - The full label that may contain " normalized by ..." text
 * @returns An object with `mainLabel` (text before the delimiter) and `normalizedSuffix`
 *          ("per ..." if present, otherwise empty string)
 */
export function extractNormalizationParts(label: string): {
  mainLabel: string;
  normalizedSuffix: string;
} {
  const normalizedIndex = label.indexOf(NORMALIZED_BY_DELIMITER);
  if (normalizedIndex < 0) {
    return { mainLabel: label, normalizedSuffix: "" };
  }
  return {
    mainLabel: label.substring(0, normalizedIndex),
    normalizedSuffix:
      "normalized by" +
      label.substring(normalizedIndex + NORMALIZED_BY_DELIMITER.length - 1)
  };
}

/**
 * Formats a label with intelligent wrapping and truncation for ML ECharts axis labels.
 * Handles both fullscreen and normal view modes, with special logic for dense datasets.
 *
 * @param options - Configuration options for label formatting
 * @returns Formatted label string (may include newlines for wrapping)
 */
export function formatAxisLabel({
  value,
  dataLength,
  isFullscreen = false,
  maxCharsPerLine = isFullscreen ? 23 : 18,
  maxLines = isFullscreen ? 3 : 2,
  maxFeaturesToAllowTextWrap = 14
}: FormatLabelOptions): string {
  // Strip _CODE suffix before formatting
  const cleanValue = stripCodeSuffix(value);

  // Extract "normalized by ..." suffix if present — it gets its own line, displayed as "per ..."
  const { mainLabel, normalizedSuffix } = extractNormalizationParts(cleanValue);

  // If mainLabel + normalized suffix fits on one line, keep together
  if (normalizedSuffix) {
    const combined = mainLabel + " " + normalizedSuffix;
    if (combined.length <= maxCharsPerLine) {
      return combined;
    }
  }

  // Wrap the normalized suffix early so we know how many lines it needs
  const wrappedSuffix = normalizedSuffix
    ? wrapSuffix(normalizedSuffix, maxCharsPerLine)
    : "";
  const suffixLineCount = wrappedSuffix ? wrappedSuffix.split("\n").length : 0;

  // Reserve lines for the normalized suffix when it won't fit alongside the main label
  const mainMaxLines = normalizedSuffix
    ? Math.max(1, maxLines - suffixLineCount)
    : maxLines;

  const formatted = formatMainLabel(mainLabel, {
    dataLength,
    isFullscreen,
    maxCharsPerLine,
    maxLines: mainMaxLines,
    maxFeaturesToAllowTextWrap
  });

  if (!normalizedSuffix) {
    return formatted;
  }

  // Check if the normalized suffix fits on the last line of the wrapped main label
  const lastNewline = formatted.lastIndexOf("\n");
  const lastLine = lastNewline >= 0 ? formatted.substring(lastNewline + 1) : formatted;
  if (lastLine.length + 1 + normalizedSuffix.length <= maxCharsPerLine) {
    return formatted + " " + normalizedSuffix;
  }

  return formatted + "\n" + wrappedSuffix;
}

function wrapSuffix(suffix: string, maxCharsPerLine: number): string {
  if (suffix.length <= maxCharsPerLine) {
    return suffix;
  }

  const NORMALIZED_BY_PREFIX = "normalized by ";
  if (suffix.startsWith(NORMALIZED_BY_PREFIX)) {
    return "normalized by\n" + suffix.substring(NORMALIZED_BY_PREFIX.length);
  }

  // Generic fallback: break at last space within maxCharsPerLine
  const spaceIndex = suffix.lastIndexOf(" ", maxCharsPerLine);
  if (spaceIndex > 0) {
    return suffix.substring(0, spaceIndex) + "\n" + suffix.substring(spaceIndex + 1);
  }
  return suffix;
}

function formatMainLabel(
  label: string,
  options: {
    dataLength: number;
    isFullscreen: boolean;
    maxCharsPerLine: number;
    maxLines: number;
    maxFeaturesToAllowTextWrap: number;
  }
): string {
  const {
    dataLength,
    isFullscreen,
    maxCharsPerLine,
    maxLines,
    maxFeaturesToAllowTextWrap
  } = options;
  const maxTotalChars = maxCharsPerLine * maxLines;

  // Don't wrap text if there are more than (default 14) feature items, and not fullscreen
  if (dataLength > maxFeaturesToAllowTextWrap && !isFullscreen) {
    return label.length > maxCharsPerLine
      ? label.substring(0, maxCharsPerLine) + "..."
      : label;
  }

  // If text fits on one line, return as is
  if (label.length <= maxCharsPerLine) {
    return label;
  }

  // Truncate if too long for allowed lines
  const textToWrap =
    label.length > maxTotalChars ? label.substring(0, maxTotalChars - 3) + "..." : label;

  // After truncation, if text now fits on one line, return as is (avoids wrapping "..." to a second line)
  if (textToWrap.length <= maxCharsPerLine) {
    return textToWrap;
  }

  // Wrap text across up to maxLines lines, preferring space breaks
  const lines: string[] = [];
  let remaining = textToWrap;

  while (lines.length < maxLines - 1 && remaining.length > maxCharsPerLine) {
    const spaceIndex = remaining.lastIndexOf(" ", maxCharsPerLine);
    const breakPoint =
      spaceIndex > maxCharsPerLine - 8 && spaceIndex > 0 ? spaceIndex : maxCharsPerLine;

    lines.push(remaining.substring(0, breakPoint));
    remaining = remaining.substring(breakPoint + (spaceIndex === breakPoint ? 1 : 0));
  }

  lines.push(remaining);
  return lines.join("\n");
}
